import { zipSync, strToU8 } from 'fflate'
/* Overgenomen uit de Exact-app (src/lib/export.ts): alleen de schrijver, geen koppeling. */
const isBedragVeld = (_veld: string) => false

/* ===========================================================================
 *  Exporteren
 *
 *  Drie vormen, en elk om een reden:
 *
 *    xlsx   Excel met echte getallen en echte datums. Optellen in Excel werkt
 *           meteen; een bedrag is geen tekst "1.234,56".
 *    csv    Voor Excel in het Nederlands: puntkomma tussen de velden, een
 *           komma in een bedrag, en een BOM vooraan zodat é en ë goed
 *           aankomen. Opent met een dubbelklik goed.
 *    json   Alles zoals het is, voor wie er zelf mee verder wil.
 *
 *  Het bestand wordt in de browser gemaakt, uit wat de server al stuurde.
 *  Er komt niets op een server te staan; wat er geëxporteerd werd staat wel
 *  in het logboek (de functie schrijft het bij de eerste bladzijde).
 *
 *  Waarom geen bibliotheek voor xlsx: de bekende (SheetJS op npm) is oud en
 *  heeft openstaande lekken, en is groot. Een xlsx is een zip met een paar
 *  XML-bestanden; voor schrijven is dit genoeg.
 * =========================================================================== */

export type Formaat = 'xlsx' | 'csv' | 'json'

export interface Kolom { veld: string; label: string }

export type Waarde = string | number | boolean | null | undefined

function bestandsnaam(naam: string, formaat: Formaat): string {
  const schoon = naam.replace(/[^\p{L}\p{N} _.-]+/gu, '').replace(/\s+/g, ' ').trim().slice(0, 80) || 'export'
  const stempel = new Date().toISOString().slice(0, 16).replace('T', ' ').replace(':', '')
  return `${schoon} ${stempel}.${formaat}`
}

function download(inhoud: BlobPart, type: string, naam: string) {
  const blob = new Blob([inhoud], { type })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = naam
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 30_000)
}

/* ------------------------------------------------------------------ *
 *  CSV
 * ------------------------------------------------------------------ */

const ISO_DATUM = /^\d{4}-\d{2}-\d{2}$/

function csvCel(w: Waarde, bedragKolom: boolean): string {
  if (w === null || w === undefined) return ''
  if (typeof w === 'number') {
    const t = bedragKolom ? w.toFixed(2) : String(w)
    return t.replace('.', ',')
  }
  if (typeof w === 'boolean') return w ? 'ja' : 'nee'
  let t = String(w)
  if (ISO_DATUM.test(t)) {
    const [j, m, d] = t.split('-')
    t = `${d}-${m}-${j}`
  }
  t = geenFormule(t)
  return /[;"\n\r]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t
}

/*
 * Een tekst die met = + - @ (of een tab of regeleinde) begint, voert Excel
 * uit als formule -- ook als hij met een cijfer verdergaat: "-1+1|cmd" is een
 * formule. Een omschrijving uit een factuur van een leverancier hoort geen
 * formule te worden. Alleen een kaal getal ("-12,50") mag zo blijven staan.
 * Geldt voor de CSV én voor kopiëren naar het klembord (plakken in Excel
 * rekent net zo goed). In de xlsx staat tekst als tekst; daar kan het niet.
 */
function geenFormule(t: string): string {
  const begin = t.replace(/^[  ]+/, '')
  if (!/^[=+\-@\t\r\n]/.test(begin)) return t
  if (/^-?\d+([.,]\d+)*$/.test(begin)) return t
  return "'" + t
}

export function naarCsv(kolommen: Kolom[], rijen: Waarde[][]): string {
  const bedragen = kolommen.map((k) => isBedragVeld(k.veld))
  const kop = kolommen.map((k) => csvCel(k.label, false)).join(';')
  const lijf = rijen.map((r) => r.map((w, i) => csvCel(w, bedragen[i] ?? false)).join(';'))
  return '﻿' + [kop, ...lijf].join('\r\n')
}

/* ------------------------------------------------------------------ *
 *  XLSX
 * ------------------------------------------------------------------ */

const xml = (t: string) => t
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  // eslint-disable-next-line no-control-regex
  .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, '')

function kolomLetter(i: number): string {
  let s = ''
  let n = i + 1
  while (n > 0) { const r = (n - 1) % 26; s = String.fromCharCode(65 + r) + s; n = Math.floor((n - 1) / 26) }
  return s
}

/* Stijlen: 0 gewoon, 1 kop (vet), 2 bedrag, 3 datum, 4 getal. */
const STIJLEN = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="1"><numFmt numFmtId="164" formatCode="dd-mm-yyyy"/></numFmts>
<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>
<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>
<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="5">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>
<xf numFmtId="4" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
</cellXfs>
</styleSheet>`

function xlsxCel(ref: string, w: Waarde, bedragKolom: boolean): string {
  if (w === null || w === undefined || w === '') return ''
  if (typeof w === 'number' && Number.isFinite(w)) return `<c r="${ref}" s="${bedragKolom ? 2 : 4}"><v>${w}</v></c>`
  if (typeof w === 'boolean') return `<c r="${ref}" t="b"><v>${w ? 1 : 0}</v></c>`
  const t = String(w)
  if (ISO_DATUM.test(t)) {
    const serieel = Date.parse(t + 'T00:00:00Z') / 86400000 + 25569
    if (Number.isFinite(serieel)) return `<c r="${ref}" s="3"><v>${serieel}</v></c>`
  }
  return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${xml(t.slice(0, 32000))}</t></is></c>`
}

function blad(kolommen: Kolom[], rijen: Waarde[][]): string {
  const bedragen = kolommen.map((k) => isBedragVeld(k.veld))
  const delen: string[] = []
  delen.push('<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">')
  delen.push('<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>')
  delen.push('<cols>' + kolommen.map((k, i) => `<col min="${i + 1}" max="${i + 1}" width="${Math.min(Math.max(k.label.length + 4, 10), 50)}" customWidth="1"/>`).join('') + '</cols>')
  delen.push('<sheetData>')
  delen.push('<row r="1">' + kolommen.map((k, i) => `<c r="${kolomLetter(i)}1" t="inlineStr" s="1"><is><t>${xml(k.label)}</t></is></c>`).join('') + '</row>')
  rijen.forEach((r, ri) => {
    const nr = ri + 2
    delen.push(`<row r="${nr}">` + r.map((w, i) => xlsxCel(`${kolomLetter(i)}${nr}`, w, bedragen[i] ?? false)).join('') + '</row>')
  })
  delen.push('</sheetData>')
  if (kolommen.length) delen.push(`<autoFilter ref="A1:${kolomLetter(kolommen.length - 1)}${rijen.length + 1}"/>`)
  delen.push('</worksheet>')
  return delen.join('')
}

export function naarXlsx(bladen: { naam: string; kolommen: Kolom[]; rijen: Waarde[][] }[]): Uint8Array {
  const namen = bladen.map((b, i) => (b.naam.replace(/[\\/?*[\]:]/g, ' ').slice(0, 31).trim() || `Blad ${i + 1}`))
  const bestanden: Record<string, Uint8Array> = {
    '[Content_Types].xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
${bladen.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('\n')}
</Types>`),
    '_rels/.rels': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`),
    'xl/workbook.xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets>${namen.map((n, i) => `<sheet name="${xml(n)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('')}</sheets>
</workbook>`),
    'xl/_rels/workbook.xml.rels': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
${bladen.map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join('\n')}
<Relationship Id="rId${bladen.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`),
    'xl/styles.xml': strToU8(STIJLEN),
  }
  bladen.forEach((b, i) => { bestanden[`xl/worksheets/sheet${i + 1}.xml`] = strToU8(blad(b.kolommen, b.rijen)) })
  return zipSync(bestanden, { level: 6 })
}

/* ------------------------------------------------------------------ *
 *  Eén ingang
 * ------------------------------------------------------------------ */

export function bewaar(naam: string, formaat: Formaat, kolommen: Kolom[], rijen: Waarde[][], ruw?: unknown) {
  const bestand = bestandsnaam(naam, formaat)
  if (formaat === 'csv') {
    download(naarCsv(kolommen, rijen), 'text/csv;charset=utf-8', bestand)
  } else if (formaat === 'json') {
    const inhoud = ruw ?? rijen.map((r) => Object.fromEntries(kolommen.map((k, i) => [k.veld, r[i] ?? null])))
    download(JSON.stringify(inhoud, null, 2), 'application/json', bestand)
  } else {
    const bytes = naarXlsx([{ naam, kolommen, rijen }])
    download(bytes as BlobPart, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', bestand)
  }
}

/** Meerdere bladen in één Excel-bestand (bijvoorbeeld de regels én de totalen). */
export function bewaarBladen(naam: string, bladen: { naam: string; kolommen: Kolom[]; rijen: Waarde[][] }[]) {
  download(naarXlsx(bladen) as BlobPart, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', bestandsnaam(naam, 'xlsx'))
}

/**
 * Een kant-en-klare tekst bewaren, met een eigen extensie.
 *
 * Voor het rapport uit Inzicht: dat is Markdown, en Markdown is geen tabel met
 * kolommen. Het loopt wel langs dezelfde bestandsnaam (met tijdstempel) en
 * dezelfde download, zodat alles uit dit venster er hetzelfde uitziet in je
 * downloadmap.
 */
export function bewaarTekst(naam: string, ext: string, inhoud: string, type = 'text/plain;charset=utf-8') {
  const basis = bestandsnaam(naam, 'csv').replace(/\.csv$/, '')
  download(inhoud, type, `${basis}.${ext}`)
}

/** Naar het klembord, als tabel: plakt in Excel meteen in kolommen. */
export async function kopieerTabel(kolommen: Kolom[], rijen: Waarde[][]): Promise<void> {
  const cel = (w: Waarde) => (w === null || w === undefined ? '' : typeof w === 'number' ? String(w).replace('.', ',') : geenFormule(String(w).replace(/[\t\r\n]+/g, ' ')))
  const tekst = [kolommen.map((k) => k.label).join('\t'), ...rijen.map((r) => r.map(cel).join('\t'))].join('\n')
  await navigator.clipboard.writeText(tekst)
}
